// Pure helpers the Update, Tile and Frame readers share: MDX props in, escaped
// HTML out, with no Tiptap or markdown parser in the import graph.

/** One JSX-style opening tag's props, quoted or in braces, allowing `>` inside quotes. */
export const PROPS = String.raw`((?:\s+[\w-]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|\{(?:[^{}]|\{[^{}]*\})*\}))?)*)\s*`

const PROP =
	/([\w-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|\{((?:[^{}]|\{[^{}]*\})*)\}))?/g

export const decodeAttribute = (value: string): string =>
	value
		.replace(/&quot;/g, '"')
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&#39;/g, "'")
		.replace(/&amp;/g, "&")

export const escapeAttribute = (value: string): string =>
	value
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")

export const escapeText = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

/**
 * String props come back decoded; a brace prop keeps its expression, except a
 * plain string literal inside the braces, which is unwrapped.
 */
export function readProps(raw: string): Record<string, string | true> {
	const out: Record<string, string | true> = {}
	for (const match of raw.matchAll(PROP)) {
		const key = match[1]
		if (!key) continue
		const quoted = match[2] ?? match[3]
		if (quoted !== undefined) {
			out[key] = decodeAttribute(quoted)
			continue
		}
		const expression = match[4]?.trim()
		if (expression === undefined) {
			out[key] = true
			continue
		}
		const literal = /^(?:"([^"]*)"|'([^']*)'|`([^`]*)`)$/.exec(expression)
		out[key] = literal
			? (literal[1] ?? literal[2] ?? literal[3] ?? "")
			: expression
	}
	return out
}

export const stringProp = (
	props: Record<string, string | true>,
	key: string,
): string | null => {
	const value = props[key]
	return typeof value === "string" ? value : null
}

/** `tags={["a", "b"]}` as a list of strings. */
export function readStringList(expression: string | null): string[] {
	if (!expression) return []
	return [...expression.matchAll(/"([^"]*)"|'([^']*)'/g)]
		.map((match) => (match[1] ?? match[2] ?? "").trim())
		.filter(Boolean)
}

// The markdown parsers wrap a single line in a paragraph, which a text-only
// line node (a caption, a title) has no room for.
export const unwrapParagraph = (html: string): string =>
	html
		.trim()
		.replace(/^<p>([\s\S]*?)<\/p>$/, "$1")
		.trim()
